/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    TextField,
    IconButton,
    Backdrop,
    FormControlLabel,
    CircularProgress,
    Checkbox,
    Typography
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { ListingActions } from 'store/slices/listings';
import { AttachmentActions } from 'store/slices/attachments';
import { dispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { openConfirmationModal } from 'store/slices/confirmationModal';
import { ENVIRONMENT } from './config';
import { toast } from 'react-toastify';
import axiosServices from 'utils/axios';

function ImageUploadComponent({ propertyId, data, historyRefresh }) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedForDeletion, setSelectedForDeletion] = useState([]);

    const { getS3PresignedUrl, createAttachment, deleteAttachment } = AttachmentActions;
    const { fetchListingDetails } = ListingActions;

    // State to store the primary image ID
    const [primaryImageId, setPrimaryImageId] = useState(null);

    useState(() => {
        if (data) {
            const primaryImage = data.find((item) => item.sequence === 0);
            setPrimaryImageId(primaryImage?.id || null);
        }
    }, [data]);

    // Handle checkbox change for primary image
    const handlePrimaryChange = (photoId) => {
        // If the same image is clicked again, unselect it
        if (primaryImageId === photoId) {
            // makePrimaryImage(photoId, false);
            setPrimaryImageId(null); // Unselect if clicked again
        } else {
            makePrimaryImage(photoId);
            setPrimaryImageId(photoId); // Set as primary
        }
    };

    const makePrimaryImage = async (id) => {
        try {
            if (!id) {
                toast.error('Id is required');
                return;
            }
            let prevData = data.find((item) => item.sequence === 0);

            if (prevData) {
                const prevDataID = prevData.id;
                prevData = {
                    title: prevData.title,
                    url: prevData.url,
                    type: prevData.type,
                    sequence: 999
                };
                const { data: dataa } = await axiosServices.patch(`attachments/${prevDataID}`, prevData);
            }

            let foundData = data.find((item) => item.id === id);
            if (!foundData) {
                toast.error('Something went wrong');
                return;
            }
            foundData = {
                title: foundData.title,
                url: foundData.url,
                type: foundData.type,
                sequence: 0
            };

            const { data: returnedData } = await axiosServices.patch(`attachments/${id}`, foundData);
            historyRefresh && historyRefresh();
        } catch (error) {
            // console.log(error);

            toast.error('Something went wrong');
        }
    };

    useEffect(() => {
        if (data) {
            setPhotos(data?.map((e) => ({ id: e?.id, url: e?.url, caption: e?.title })));
        }
    }, [data]);

    const handleSave = async () => {
        if (photos.length > 31) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'You can only upload a maximum of 31 images',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: true
                })
            );
            return;
        }

        setLoading(true);
        try {
            const uploadPromises = photos?.map(async ({ file, caption = '' }, index) => {
                if (!file) return null;

                const key = `attachment_${Date.now() + index}`;
                const s3Url = await dispatch(getS3PresignedUrl(key));

                if (!s3Url?.payload) {
                    throw new Error('S3 URL not generated');
                }

                // Upload the file to S3 using the presigned URL
                const response = await fetch(s3Url?.payload, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': file.type // Set the file type
                    },
                    body: file
                });

                if (!response.ok) {
                    throw new Error('Failed to upload to S3');
                }

                // Save the file URL and caption
                return dispatch(
                    createAttachment({
                        title: caption,
                        property_id: propertyId,
                        url: key,
                        type: file?.type
                    })
                );
            });

            // Wait for all uploads to complete
            await Promise.all(uploadPromises);
            dispatch(
                openSnackbar({
                    open: true,
                    message: `Image(s) uploaded successfully`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: true
                })
            );

            setPhotos([]);
            dispatch(fetchListingDetails(propertyId));
        } catch (error) {
            console.error(error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Failed to upload images',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: true
                })
            );
        } finally {
            setLoading(false);
        }
    };

    const handleUploadChange = (event) => {
        const files = event.target.files;
        const newPhotos = Array.from(files).map((file) => ({
            file,
            caption: '',
            id: URL.createObjectURL(file) // temp ID based on URL
        }));
        setPhotos((prev) => [...newPhotos, ...prev]);
    };

    const handleCaptionChange = (id, caption) => {
        setPhotos((prev) => prev.map((photo) => (photo.id === id ? { ...photo, caption } : photo)));
    };

    const handleDeleteSelection = (id) => {
        setSelectedForDeletion((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const handleDeleteImages = async () => {
        try {
            if (selectedForDeletion?.length === 0) {
                throw new Error('Select attachments to delete');
            }

            const object = {
                id: selectedForDeletion
            };

            dispatch(
                openConfirmationModal({
                    open: true,
                    message: 'Are you sure you want to delete',
                    islistingNumber: true,
                    handleConfirm: async () => {
                        const res = await dispatch(deleteAttachment(object));

                        if (res?.payload?.status !== 201 && res?.payload?.status !== 200) {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message: res?.payload?.message || 'Internal server error',
                                    variant: 'alert',
                                    alert: {
                                        color: 'error'
                                    },
                                    close: true
                                })
                            );
                        } else {
                            dispatch(
                                openSnackbar({
                                    open: true,
                                    message: 'Attachments deleted successfully',
                                    variant: 'alert',
                                    alert: {
                                        color: 'success'
                                    },
                                    close: true
                                })
                            );
                            dispatch(fetchListingDetails(propertyId));
                            setPhotos((prev) => prev.filter((photo) => !selectedForDeletion.includes(photo.id)));
                            setSelectedForDeletion([]);
                        }
                    }
                })
            );

            return null;
        } catch (error) {
            console.error('Error deleting images:', error);
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || 'Internal server error',
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: true
                })
            );
            return null;
        }
    };

    const handleDeleteRecentUpload = (id) => {
        setPhotos((prev) => prev.filter((photo) => photo.id !== id));
        return null; // Ensure function returns null
    };

    return (
        <Box sx={{ mx: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Box>
                    <Typography align="left" variant="h3" component="div" sx={{ my: 2, fontWeight: '700' }}>
                        Upload Property Photos<span style={{ color: 'red' }}>&#42;</span>
                    </Typography>
                    <Typography align="left" variant="subtitle1" component="div" color="red" sx={{ my: 2, textTransform: 'capitalize' }}>
                        NOTE: Please ensure that you save the attachment before submitting.
                    </Typography>
                </Box>

                <Box>
                    <label htmlFor="iconButton">
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="iconButton"
                            type="file"
                            multiple
                            onChange={handleUploadChange}
                        />
                        <Button variant="contained" component="span" sx={{ background: '#0a66c2', py: 1.25 }} startIcon={<PhotoCamera />}>
                            Upload Image
                        </Button>
                    </label>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {selectedForDeletion?.length !== 0 && (
                            <Button
                                variant="contained"
                                sx={{ background: '#ff3644', py: 1.15, mt: 2 }}
                                onClick={handleDeleteImages}
                                startIcon={<DeleteIcon />}
                            >
                                Delete Image
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mt: 2, minHeight: '20vh' }}>
                {photos?.map((photo, index) => (
                    <Grid item xs={12} sm={6} lg={3} key={photo.id}>
                        <Box
                            sx={{
                                p: 1,
                                position: 'relative',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Primary image checkbox */}
                            {!photo.file && (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedForDeletion.includes(photo?.id)}
                                                onChange={() => handleDeleteSelection(photo?.id)}
                                                name={`select-${photo.id}`}
                                                // color="error"
                                                // sx={{ color: 'red' }}
                                            />
                                        }
                                        label={`Slot: ${index + 1}`}
                                        sx={{ mx: 0.1 }}
                                    />
                                </>
                            )}
                            <Box
                                component="img"
                                src={
                                    photo.file
                                        ? photo?.id
                                        : ENVIRONMENT === 'TESTING'
                                        ? `https://mls-development-bucket.s3.us-east-1.amazonaws.com/${photo?.url}`
                                        : `https://mlsapartments-images.s3.ap-south-1.amazonaws.com/${photo?.url}`
                                }
                                alt={`Uploaded ${index}`}
                                sx={{
                                    width: '100%',
                                    borderRadius: '4px',
                                    objectFit: 'cover',
                                    height: 250
                                }}
                            />
                            {photo.file ? (
                                <>
                                    <TextField
                                        fullWidth
                                        placeholder="Add a caption (optional)"
                                        variant="outlined"
                                        value={photo.caption}
                                        onChange={(e) => handleCaptionChange(photo.id, e.target.value)}
                                        sx={{ mt: 1 }}
                                    />
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => handleDeleteRecentUpload(photo?.id)}
                                        sx={{ position: 'absolute', top: 8, right: 8 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={primaryImageId === photo.id} // Mark as checked if it's the primary image
                                                onChange={() => handlePrimaryChange(photo.id)} // Handle change to select as primary
                                                name={`primary-${photo.id}`}
                                                // color="error"
                                                // sx={{ color: 'red' }}
                                            />
                                        }
                                        label="Marks as Primary Image"
                                        sx={{ mx: 0.1 }}
                                    />
                                    <Typography sx={{ m: 1 }}>{photo?.caption ?? 'No Caption'}</Typography>
                                </>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Box>
                <Button
                    disabled={!photos?.filter((e) => Boolean(e?.file))?.length > 0}
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    sx={{ width: '100%', background: '#0a66c2', py: 1.25, mt: 5 }}
                >
                    Save Images/Attachments
                </Button>
            </Box>

            {loading && (
                <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </Box>
    );
}

export default ImageUploadComponent;
